/**
 * ListDetailPage - Individual list view
 *
 * Layout + state wiring only: the toolbar, the three view modes, the add-cards panel and the
 * card details panel each own their own behaviour.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAtomValue } from 'jotai';
import { FaArrowLeft, FaEdit, FaFileExport, FaFileImport, FaPlus, FaSearch, FaTrash } from 'react-icons/fa';

import useUserSettings from '@/features/settings/hooks/api';
import { formatPrice } from '@/lib/price';
import { Link, useNavigate, useParams } from 'react-router';

import Button from '@/components/Button/Button';
import EmptyState from '@/components/EmptyState/EmptyState';
import Modal from '@/components/Modal/Modal';
import type { EnrichedListCard } from '@/types/lists';

import EditListModal from './components/EditListModal';
import ExportListModal from './components/ExportListModal';
import ImportListModal from './components/ImportListModal';
import ListCardDetailsPanel from './components/ListCardDetailsPanel';
import ListCardDiscoveryPanel from './components/ListCardDiscoveryPanel';
import ListCompactView from './components/ListCompactView';
import ListGridView from './components/ListGridView';
import ListTableView from './components/ListTableView';
import ListViewControls from './components/ListViewControls';
import { useDeleteList, useList } from './hooks/api';
import useFilteredListCards from './hooks/useFilteredListCards';
import useListCardGroups from './hooks/useListCardGroups';
import useListCardOperations from './hooks/useListCardOperations';
import { enrichedListCardsByListAtom } from './state/enrichedListCards';
import {
  listAvailabilityFilterAtom,
  listGroupByAtom,
  listPriorityFilterAtom,
  listSearchQueryAtom,
  listSortAtom,
  listViewModeAtom,
} from './state/listViewSettings';

// ============================================================================
// Component
// ============================================================================

export default function ListDetailPage() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { settings } = useUserSettings();

  const list = useList(listId ?? '');
  const enrichedByList = useAtomValue(enrichedListCardsByListAtom);

  const viewMode = useAtomValue(listViewModeAtom);
  const groupBy = useAtomValue(listGroupByAtom);
  const sort = useAtomValue(listSortAtom);
  const search = useAtomValue(listSearchQueryAtom);
  const priorities = useAtomValue(listPriorityFilterAtom);
  const availability = useAtomValue(listAvailabilityFilterAtom);

  const deleteListFn = useDeleteList();
  const cardOps = useListCardOperations();

  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const cards = useMemo(() => enrichedByList.get(listId ?? '') ?? [], [enrichedByList, listId]);
  const filteredCards = useFilteredListCards(cards, { search, priorities, availability });
  const groups = useListCardGroups(filteredCards, groupBy, sort);

  const listedScryfallIds = useMemo(() => new Set(cards.map((card) => card.scryfall_id)), [cards]);

  const stats = useMemo(() => {
    let totalPrice = 0;
    let unpricedCount = 0;

    for (const card of cards) {
      if (card.total_price == null) {
        unpricedCount++;
      } else {
        totalPrice += card.total_price;
      }
    }

    return { totalCards: cards.length, totalPrice, unpricedCount };
  }, [cards]);

  const selectedCard = useMemo(() => cards.find((c) => c.id === selectedCardId) ?? null, [cards, selectedCardId]);

  useEffect(() => {
    if (!isAddPanelOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsAddPanelOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isAddPanelOpen]);

  const handleDelete = async () => {
    if (!listId) return;
    setIsDeleting(true);
    try {
      await deleteListFn(listId);
      navigate('/lists');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDetails = useCallback((card: EnrichedListCard) => setSelectedCardId(card.id), []);

  if (!list) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          title="List not found"
          description="This list may have been deleted."
          action={
            <Link to="/lists">
              <Button text="Back to Lists" icon={<FaArrowLeft />} onClick={() => {}} />
            </Link>
          }
        />
      </div>
    );
  }

  const showGroupHeaders = groupBy !== 'none';

  const renderContent = () => {
    if (cards.length === 0) {
      return (
        <EmptyState
          title="No cards in this list"
          description="Add cards to start tracking what you want to acquire."
          icon={<FaSearch className="h-8 w-8" />}
          action={<Button text="Add Cards" icon={<FaPlus />} onClick={() => setIsAddPanelOpen(true)} />}
        />
      );
    }

    if (filteredCards.length === 0) {
      return (
        <EmptyState
          title="No cards match your filters"
          description="Use Clear filters in the toolbar above to see the rest of this list."
          icon={<FaSearch className="h-8 w-8" />}
        />
      );
    }

    if (viewMode === 'list') {
      return <ListCompactView groups={groups} showGroupHeaders={showGroupHeaders} onOpenDetails={openDetails} />;
    }

    if (viewMode === 'grid') {
      return (
        <ListGridView
          groups={groups}
          showGroupHeaders={showGroupHeaders}
          isPending={cardOps.isPending}
          onIncrease={(card) => cardOps.increase(card.id, card.quantity)}
          onDecrease={(card) => cardOps.decrease(card.id, card.quantity)}
          onRemove={(card) => cardOps.remove(card.id)}
          onOpenDetails={openDetails}
        />
      );
    }

    return (
      <ListTableView
        groups={groups}
        showGroupHeaders={showGroupHeaders}
        isPending={cardOps.isPending}
        onIncrease={(card) => cardOps.increase(card.id, card.quantity)}
        onDecrease={(card) => cardOps.decrease(card.id, card.quantity)}
        onRemove={(card) => cardOps.remove(card.id)}
        onPriorityChange={(card, priority) => cardOps.setPriority(card.id, priority)}
        onNotesChange={(card, notes) => cardOps.setNotes(card.id, notes)}
        onOpenDetails={openDetails}
      />
    );
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="border-b border-zinc-800 px-6 py-4">
        <div className="flex items-center gap-4">
          <Link to="/lists" className="text-ash hover:text-pearl transition-colors" aria-label="Back to lists">
            <FaArrowLeft className="h-4 w-4" />
          </Link>

          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg text-lg"
              style={list.color ? { backgroundColor: list.color } : { backgroundColor: 'var(--color-slate)' }}
            >
              {list.icon || '📋'}
            </div>
            <div>
              <h1 className="text-xl font-semibold text-zinc-100">{list.name}</h1>
              <p className="text-sm text-zinc-500">
                {stats.totalCards} {stats.totalCards === 1 ? 'card' : 'cards'}
                {stats.totalPrice > 0 && <span className="text-emerald-400"> · {formatPrice(stats.totalPrice, settings.currency)}</span>}
                {stats.unpricedCount > 0 && <span> · {stats.unpricedCount} unpriced</span>}
              </p>
            </div>
          </div>

          <div className="flex-1" />

          <Button
            icon={<FaPlus />}
            text="Add Cards"
            size="small"
            color={isAddPanelOpen ? 'primary' : 'secondary'}
            onClick={() => setIsAddPanelOpen((open) => !open)}
          />

          <div className="h-6 w-px bg-zinc-700" />

          <div className="flex items-center gap-2">
            <Button
              icon={<FaFileImport />}
              text="Import"
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => setIsImportModalOpen(true)}
            />
            <Button
              icon={<FaFileExport />}
              text="Export"
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => setIsExportModalOpen(true)}
            />
          </div>

          <div className="h-6 w-px bg-zinc-700" />

          <Button
            icon={<FaEdit />}
            text="Edit"
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setIsEditModalOpen(true)}
          />
          <Button
            icon={<FaTrash />}
            text="Delete"
            variant="outlined"
            color="delete"
            size="small"
            onClick={() => setIsDeleteModalOpen(true)}
          />
        </div>
      </header>

      <ListViewControls />

      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 overflow-auto p-6">{renderContent()}</main>

        {isAddPanelOpen && listId && (
          <aside className="w-80 shrink-0 overflow-hidden border-l border-zinc-800 bg-zinc-900/30" aria-label="Add cards">
            <ListCardDiscoveryPanel listId={listId} listedScryfallIds={listedScryfallIds} onClose={() => setIsAddPanelOpen(false)} />
          </aside>
        )}
      </div>

      <EditListModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} list={list} />

      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Delete List" size="sm">
        <div className="space-y-4">
          <p className="text-silver">
            Are you sure you want to delete <strong className="text-pearl">{list.name}</strong>? This will also delete all{' '}
            {stats.totalCards} cards in this list.
          </p>
          <div className="border-storm flex justify-end gap-3 border-t pt-4">
            <Button text="Cancel" variant="text" onClick={() => setIsDeleteModalOpen(false)} />
            <Button text={isDeleting ? 'Deleting...' : 'Delete'} color="delete" onClick={handleDelete} disabled={isDeleting} />
          </div>
        </div>
      </Modal>

      <ImportListModal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} listId={listId} />

      <ExportListModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        list={list}
        cards={cards}
        filteredCards={filteredCards}
      />

      <ListCardDetailsPanel card={selectedCard} listName={list.name} onClose={() => setSelectedCardId(null)} />
    </div>
  );
}
